var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { validateProducts, validateFilters, validateSearchMeta, validateSortOptions } from '../utils/validation';
/**
 * Enrich products with variant data using the global variant fetching function
 */
export function enrichProductsWithVariants(products) {
    return __awaiter(this, void 0, void 0, function* () {
        if (typeof window === 'undefined') {
            return products;
        }
        // Check which products are missing variant data
        const missingVariantIds = products
            .filter((product) => {
            var _a, _b;
            const hasVariants = window.getProductVariants
                ? (window.getProductVariants(product.id) || []).length > 0
                : (((_b = (_a = window.productVariantMap) === null || _a === void 0 ? void 0 : _a[product.id]) === null || _b === void 0 ? void 0 : _b.variants) || []).length > 0;
            return !hasVariants;
        })
            .map((product) => product.id);
        if (missingVariantIds.length === 0) {
            console.log('All products already have variant data');
            return products;
        }
        try {
            console.log(`Enriching ${missingVariantIds.length} products with variant data`);
            // Use the global function to fetch missing variant data
            if (window.fetchProductVariants) {
                const newVariantData = yield window.fetchProductVariants(missingVariantIds);
                console.log('Received new variant data:', newVariantData);
            }
            else {
                console.warn('window.fetchProductVariants not available');
            }
            // Return updated products with the new variant data from the global map
            const enrichedProducts = products.map((product) => {
                var _a, _b;
                const rawVariants = window.getProductVariants
                    ? window.getProductVariants(product.id) || []
                    : ((_b = (_a = window.productVariantMap) === null || _a === void 0 ? void 0 : _a[product.id]) === null || _b === void 0 ? void 0 : _b.variants) || [];
                // Convert Algolia variants to match the Product interface
                const variants = rawVariants.map((v) => ({
                    id: v.id,
                    title: v.title,
                    price: {
                        regular: parseFloat(v.price) || 0,
                        sale: v.compareAtPrice ? parseFloat(v.compareAtPrice) : undefined
                    },
                    available: v.available,
                    sku: undefined,
                    inventoryQuantity: undefined,
                    selectedOptions: undefined,
                    image: undefined
                }));
                return Object.assign(Object.assign({}, product), { variants: variants });
            });
            console.log('Products enriched with variants:', enrichedProducts);
            return enrichedProducts;
        }
        catch (error) {
            console.error('Failed to enrich products with variant data:', error);
            return products;
        }
    });
}
/**
 * Get facet configuration from metafields
 * Returns a map of facet names to their configuration
 */
function getFacetConfigMap() {
    var _a, _b, _c;
    const configMap = {};
    // Try to access facets from SHOP_DATA first (primary method)
    if (typeof window !== 'undefined' && ((_a = window.SHOP_DATA) === null || _a === void 0 ? void 0 : _a.facets)) {
        const facetConfigs = window.SHOP_DATA.facets;
        facetConfigs.forEach((facet) => {
            configMap[facet.name] = facet;
        });
        return configMap;
    }
    // Fallback: Try to access metafields from global window object
    if (typeof window !== 'undefined' &&
        ((_c = (_b = window.shopMetafields) === null || _b === void 0 ? void 0 : _b.algolia_search) === null || _c === void 0 ? void 0 : _c.collection_facets)) {
        const facetConfigs = window.shopMetafields.algolia_search.collection_facets;
        facetConfigs.forEach((facet) => {
            configMap[facet.name] = facet;
        });
        return configMap;
    }
    // If no metafields available, check if data is injected via a script tag or data attribute
    try {
        // Check for data attribute first
        const metaElement = document.querySelector('[data-algolia-facets]');
        if (metaElement) {
            const facetData = JSON.parse(metaElement.getAttribute('data-algolia-facets') || '[]');
            facetData.forEach((facet) => {
                configMap[facet.name] = facet;
            });
            return configMap;
        }
        // Check for script tag with JSON configuration
        const configScript = document.getElementById('algolia-facets-config');
        if (configScript && configScript.textContent) {
            const facetData = JSON.parse(configScript.textContent);
            facetData.forEach((facet) => {
                configMap[facet.name] = facet;
            });
            return configMap;
        }
    }
    catch (error) {
        console.warn('Failed to parse facet configuration:', error);
    }
    return configMap;
}
/**
 * Map Algolia hits to Product objects and store hits for variant fetching
 */
export function mapProducts(algoliaHits) {
    // Store the Algolia hits for variant fetching
    if (typeof window !== 'undefined' && window.updateAlgoliaHits) {
        window.updateAlgoliaHits(algoliaHits);
    }
    const products = algoliaHits.map((hit) => {
        var _a, _b, _c, _d, _e;
        // Get existing variants from the global map
        const existingVariants = window.getProductVariants
            ? window.getProductVariants(hit.id) || []
            : (window.productVariantMap && ((_a = window.productVariantMap[hit.id]) === null || _a === void 0 ? void 0 : _a.variants)) || [];
        // Ensure size is always an array
        let sizeArray = [];
        if (hit.sizes) {
            sizeArray = Array.isArray(hit.sizes) ? hit.sizes : [hit.sizes];
        }
        else if (hit.size) {
            sizeArray = Array.isArray(hit.size) ? hit.size : [hit.size];
        }
        // Ensure availability uses correct string literal type
        const availability = hit.in_stock || hit.inStock ? 'in_stock' : 'out_of_stock';
        return {
            id: hit.id,
            title: hit.title || hit.name || '',
            handle: hit.handle || hit.objectID.toLowerCase().replace(/\s+/g, '-'),
            brand: hit.vendor || hit.brand || '',
            color: ((_c = (_b = hit.meta) === null || _b === void 0 ? void 0 : _b.custom) === null || _c === void 0 ? void 0 : _c.metafield_product_colour) || hit.color || '',
            size: sizeArray,
            price: {
                regular: hit.price || 0,
                sale: hit.sale_price || hit.salePrice,
                special: hit.special_price || hit.specialPrice,
            },
            image: typeof hit.image_url === 'string'
                ? { url: hit.image_url, alt: hit.title || hit.name || '' }
                : { url: hit.image || '', alt: hit.title || hit.name || '' },
            availability: availability,
            promoMessage: hit.promo_message || hit.promoMessage || '',
            primary_color: ((_e = (_d = hit.meta) === null || _d === void 0 ? void 0 : _d.custom) === null || _e === void 0 ? void 0 : _e.metafield_primary_colour) || '',
            variants: existingVariants || [],
        };
    });
    console.log('Mapped products:', products);
    // Validate all products before returning
    return validateProducts(products, 'Algolia');
}
// /**
//  * Map products and enrich with variants - this is the main function to call
//  */
export function mapAndEnrichProducts(algoliaHits) {
    return __awaiter(this, void 0, void 0, function* () {
        // First map the basic product data
        const products = mapProducts(algoliaHits);
        // Then enrich with variant data
        // const enrichedProducts = await enrichProductsWithVariants(products);
        return products;
    });
}
export function mapFilters(algoliaFacets = {}, facetsStats, selectedFilters, facetConfig) {
    const filters = [];
    // Create a map from the passed facetConfig for easy lookup
    const facetConfigMap = {};
    if (facetConfig) {
        facetConfig.forEach((config) => {
            facetConfigMap[config.name] = config;
        });
    }
    // Fallback to the global config if no facetConfig passed
    if (Object.keys(facetConfigMap).length === 0) {
        const globalConfigMap = getFacetConfigMap();
        Object.assign(facetConfigMap, globalConfigMap);
    }
    if (facetsStats === null || facetsStats === void 0 ? void 0 : facetsStats.price) {
        const priceStats = facetsStats.price;
        let currentMin = priceStats.min;
        let currentMax = priceStats.max;
        if (selectedFilters === null || selectedFilters === void 0 ? void 0 : selectedFilters.price) {
            const prices = selectedFilters.price[0].split(',');
            currentMin = Number(prices[0]);
            currentMax = Number(prices[1]);
        }
        // Check if price facet has custom configuration
        const priceConfig = facetConfigMap['price'];
        const priceLabel = (priceConfig === null || priceConfig === void 0 ? void 0 : priceConfig.title) || 'Price';
        // Calculate a gap that allows reaching the exact max value
        // Use a reasonable step size that doesn't cause precision issues
        const range = priceStats.max - priceStats.min;
        let gap = Math.max(1, Math.floor(range / 100)); // Use 100 steps for better precision
        // Ensure the gap allows reaching the exact max value
        // If the range doesn't divide evenly, adjust the gap
        if ((range % gap) !== 0) {
            // Find a gap that divides evenly or use 1 for maximum precision
            const divisors = [1, 5, 10, 25, 50, 100, 250, 500, 1000];
            gap = divisors.find(d => range % d === 0) || Math.max(1, Math.floor(range / 50));
        }
        filters.push({
            id: 'price',
            label: priceLabel,
            type: 'range',
            max: priceStats.max,
            min: priceStats.min,
            gap: gap,
            currentMin,
            currentMax,
        });
    }
    Object.entries(algoliaFacets).forEach(([facetName, facetValues]) => {
        if (facetName === 'price')
            return;
        // Get label from the passed facetConfig first, then fallback to metafields configuration
        const facetConfiguration = facetConfigMap[facetName];
        let facetLabel;
        if (facetConfiguration === null || facetConfiguration === void 0 ? void 0 : facetConfiguration.title) {
            facetLabel = facetConfiguration.title;
        }
        else {
            // Fallback to hardcoded labels for backwards compatibility
            const facetLabels = {
                vendor: 'Brand',
                product_type: 'Product Type',
                'meta.custom.metafield_product_colour': 'Color',
                'meta.custom.metafield_age_group': 'Age Group',
                'meta.custom.metafield_gender': 'Gender',
            };
            facetLabel =
                facetLabels[facetName] ||
                    facetName.charAt(0).toUpperCase() +
                        facetName.slice(1).replace('_', ' ');
        }
        // Determine filter type based on configuration or facet name
        let filterType = 'text';
        if (facetName === 'meta.custom.metafield_product_colour' ||
            facetName.toLowerCase().includes('color') ||
            facetName.toLowerCase().includes('colour')) {
            filterType = 'color';
        }
        const options = Object.entries(facetValues).map(([value, count]) => {
            var _a;
            return ({
                id: value,
                label: value,
                count: count,
                selected: ((_a = selectedFilters === null || selectedFilters === void 0 ? void 0 : selectedFilters[facetName]) === null || _a === void 0 ? void 0 : _a.includes(value)) || false,
                value: value,
            });
        });
        // Only include facets that have options (filter out empty facets)
        if (options.length > 0) {
            filters.push({
                id: facetName,
                label: facetLabel,
                type: filterType,
                options,
            });
        }
    });
    // Validate all filters before returning
    return validateFilters(filters, 'Algolia');
}
export function mapMeta(data) {
    const meta = {
        totalResults: data.nbHits,
        totalPages: data.nbPages,
        currentPage: data.page + 1,
        pageSize: data.hitsPerPage,
        queryID: data.queryID,
    };
    // Validate meta before returning (queryID is optional so validation should still pass)
    const validatedMeta = validateSearchMeta(meta, 'Algolia');
    return Object.assign(Object.assign({}, validatedMeta), { queryID: data.queryID });
}
export function mapSorts() {
    const sorts = [
        { id: 'relevance', label: 'Relevance' },
        { id: 'title_asc', label: 'Title: A to Z' },
        { id: 'title_desc', label: 'Title: Z to A' },
        { id: 'price_asc', label: 'Price: Low to High' },
        { id: 'price_desc', label: 'Price: High to Low' },
    ];
    // Validate sort options before returning
    return validateSortOptions(sorts, 'Algolia');
}
